import { useEffect, useState } from "react";
import { Avatar, Box, SxProps, Theme, keyframes } from "@mui/material";
import User from "data/firestore/user";
import Logger from "utils/logger";
import { bustProfileUrl } from "utils/profilePhoto";
import { FIREBASE_CONFIG } from "config/app";
import { IconCamera } from "@tabler/icons-react";
import { green, orange, teal, yellow } from "@mui/material/colors";

// 1. Optimized Rotation with hardware acceleration hints
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Ripple animation for active indicator
const ripple = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(2.5);
    opacity: 0;
  }
`;

const CACHE_KEY = "validProfilePictures";

const getValidPictureCache = (): Set<string> => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    return cached ? new Set(JSON.parse(cached)) : new Set();
  } catch (error) {
    Logger.warn("Failed to load profile picture cache:", error);
    return new Set();
  }
};

const setValidPictureCache = (cache: Set<string>): void => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(Array.from(cache)));
  } catch (error) {
    Logger.warn("Failed to save profile picture cache:", error);
  }
};

// One in-memory copy of the cache so concurrent probes don't clobber each other's
// localStorage writes.
let memCache: Set<string> | null = null;
const cache = (): Set<string> => (memCache ??= getValidPictureCache());

const profilePictureUrlFor = (uid: string): string =>
  bustProfileUrl(
    `https://firebasestorage.googleapis.com/v0/b/${FIREBASE_CONFIG.storageBucket}/o/profile%2F${uid}?alt=media`
  );

// Probe (and, on success, browser-cache) a uid's real picture. Resolves whether
// it exists; the result is remembered so a card starts with the right src.
const inFlight = new Map<string, Promise<boolean>>();
function probePicture(uid: string): Promise<boolean> {
  if (!uid) return Promise.resolve(false);
  if (cache().has(uid)) return Promise.resolve(true);
  const existing = inFlight.get(uid);
  if (existing) return existing;
  const p = new Promise<boolean>((resolve) => {
    const probe = new Image();
    probe.onload = () => {
      cache().add(uid);
      setValidPictureCache(cache());
      resolve(true);
    };
    probe.onerror = () => resolve(false);
    probe.src = profilePictureUrlFor(uid);
  }).finally(() => inFlight.delete(uid));
  inFlight.set(uid, p);
  return p;
}

// Preload avatars for a list of users so the image is already fetched and its real
// src known by the time the user scrolls to that card -- no visible generic->real
// swap. Call it as soon as a list of users is available.
//
// Probes run in bounded batches: firing one Image() per user at once (100+ on
// the community/feed lists) saturates the browser's connection pool and starves
// the actual data requests. ponytail: fixed batch size, fine for these list
// sizes; switch to a streaming pool if lists ever get much larger.
const PROBE_BATCH = 8;
export async function preloadProfilePictures(uids: string[]): Promise<void> {
  for (let i = 0; i < uids.length; i += PROBE_BATCH) {
    await Promise.all(
      uids.slice(i, i + PROBE_BATCH).map((uid) => probePicture(uid))
    );
  }
}

export interface ProfilePictureProps {
  uid: string;
  alt?: string;
  hasPublicImages?: boolean;
  supporter?: boolean;
  isActive?: boolean;
  sx?: SxProps<Theme>;
}

const ProfilePicture = ({
  uid,
  alt,
  hasPublicImages,
  supporter,
  isActive,
  ...props
}: ProfilePictureProps) => {
  // Start with the real picture if we already know it exists (usually true once
  // the list has preloaded), otherwise the generic face -- which stays as the
  // fallback if no real picture exists. The probe below confirms/updates it.
  const [chosenSrc, setChosenSrc] = useState(
    uid && cache().has(uid)
      ? profilePictureUrlFor(uid)
      : User.profilePictureGeneric
  );

  useEffect(() => {
    if (!uid) {
      setChosenSrc(User.profilePictureGeneric);
      return;
    }
    probePicture(uid).then((exists) =>
      setChosenSrc(
        exists ? profilePictureUrlFor(uid) : User.profilePictureGeneric
      )
    );
  }, [uid]);

  // Size Calculation
  const sxProps = props.sx as any;
  const size = sxProps?.width || sxProps?.height || 40;
  const borderWidth = Math.max(2, Math.round(size * 0.08)); // Slightly thicker for better visual
  const outerSize = size + borderWidth * 2;

  // 2. Smoother Gradient stops
  const color1 = orange[300];
  const color2 = yellow[500];
  const smoothGradient = `conic-gradient(from 0deg, ${color1}, ${color2}, ${color1})`;

  const avatarNode = <Avatar {...props} src={chosenSrc} alt={alt} />;

  return (
    <Box sx={{ position: "relative", display: "inline-block", lineHeight: 0 }}>
      {/* Every avatar reserves the same outer footprint as a ringed one, so the
          layout never shifts when the supporter flag loads. Only supporters get
          the actual ring drawn; everyone else keeps their avatar centered in the
          same-sized transparent box. */}
      <Box
        sx={{
          position: "relative",
          width: outerSize,
          height: outerSize,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          overflow: "hidden", // Ensures nothing leaks outside the circle
        }}
      >
        {supporter && (
          <>
            {/* Animated Ring */}
            <Box
              sx={{
                position: "absolute",
                width: "150%", // Overflowing helps prevent "edge flickering" during rotation
                height: "150%",
                background: smoothGradient,
                animation: `${spin} 4s linear infinite`,
                // willChange: "transform", // Forces GPU usage
                backfaceVisibility: "hidden",
              }}
            />

            {/* Inner Mask (The Background behind Avatar) */}
            <Box
              sx={{
                position: "absolute",
                inset: borderWidth,
                borderRadius: "50%",
                backgroundColor: "background.paper", // Automatically adapts to light/dark mode
                zIndex: 1,
              }}
            />
          </>
        )}

        <Box sx={{ position: "relative", zIndex: 2 }}>{avatarNode}</Box>
      </Box>

      {/* Needs-support badge hidden: the legacy user-doc flag is stale; community
          support returns from a dedicated table later. */}
      {isActive && (
        <Box
          sx={{
            position: "absolute",
            bottom: -5,
            right: -5,
            width: 18,
            height: 18,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Ripple effect */}
          <Box
            sx={{
              position: "absolute",
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: green[500],
              animation: `${ripple} 2s ease-out infinite`,
            }}
          />
          {/* Solid dot */}
          <Box
            sx={{
              width: 15,
              height: 15,
              borderRadius: "50%",
              backgroundColor: green[500],
              border: "2px solid white",
              boxShadow: "0 0 0 1px rgba(0,0,0,0.1)",
            }}
          />
        </Box>
      )}
      {hasPublicImages && (
        <Box
          sx={{
            position: "absolute",
            bottom: -5,
            left: -5,
            width: "20px",
            height: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: teal[500],
          }}
        >
          <IconCamera size={18} />
        </Box>
      )}
    </Box>
  );
};

export default ProfilePicture;
